import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import ImageIcon from "@mui/icons-material/Image";
import { STATIC_URL } from "../config";
import api from "../utils/api";
import { formatProductForXenForo } from "../utils/xenForoFormatters";
import { generateProductDescription } from "../utils/aiGenerator";

function Products() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    status: "Undetected",
    images: [],
    video: {
      url: "",
      file: "",
    },
    features: [],
    systemRequirements: [],
    paymentLinks: [],
    tags: [],
    isBestSeller: false,
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      setFormData({
        name: selectedProduct.name || "",
        category: selectedProduct.category?._id || "",
        description: selectedProduct.description || "",
        status: selectedProduct.status || "Undetected",
        images: selectedProduct.images || [],
        video: selectedProduct.video || { url: "", file: "" },
        features: selectedProduct.features || [],
        systemRequirements: selectedProduct.systemRequirements
          ? selectedProduct.systemRequirements
              .map((req) =>
                typeof req === "string"
                  ? req.replace(/[\[\]"]/g, "").trim()
                  : ""
              )
              .filter(Boolean)
          : [],
        paymentLinks: selectedProduct.paymentLinks || [],
        tags: selectedProduct.tags || [],
        isBestSeller: selectedProduct.isBestSeller || false,
      });
      setExistingImages(selectedProduct.images || []);
    } else {
      setFormData({
        name: "",
        category: "",
        description: "",
        status: "Undetected",
        images: [],
        video: { url: "", file: "" },
        features: [],
        systemRequirements: [],
        paymentLinks: [],
        tags: [],
        isBestSeller: false,
      });
      setExistingImages([]);
    }
    setImageFiles([]);
    setVideoFile(null);
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await api.delete(`/products/${id}`);
        fetchProducts();
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("status", formData.status);
      formDataToSend.append("isBestSeller", formData.isBestSeller);
      formDataToSend.append("features", JSON.stringify(formData.features));
      // Отправляем как массив строк без дополнительного преобразования
      formData.systemRequirements.forEach((req, index) => {
        formDataToSend.append(`systemRequirements[${index}]`, req);
      });
      formDataToSend.append(
        "paymentLinks",
        JSON.stringify(formData.paymentLinks)
      );
      formDataToSend.append("tags", JSON.stringify(formData.tags));
      formDataToSend.append("videoUrl", formData.video.url);
      formDataToSend.append("existingVideoFile", formData.video.file);
      formDataToSend.append("existingImages", JSON.stringify(existingImages));

      // Добавляем новые изображения
      imageFiles.forEach((file) => {
        formDataToSend.append("images", file);
      });

      // Добавляем видео файл если есть
      if (videoFile) {
        formDataToSend.append("video", videoFile);
      }

      if (selectedProduct) {
        await api.put(`/products/${selectedProduct._id}`, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await api.post("/products", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      fetchProducts();
      setOpenDialog(false);
    } catch (error) {
      console.error("Error submitting product:", error);
    }
  };

  const handleCopyToClipboard = (product) => {
    const formattedText = formatProductForXenForo(product);
    navigator.clipboard.writeText(formattedText).then(
      () => {
        setSnackbar({
          open: true,
          message: "Текст скопирован в буфер обмена",
          severity: "success",
        });
      },
      () => {
        setSnackbar({
          open: true,
          message: "Ошибка при копировании текста",
          severity: "error",
        });
      }
    );
  };

  const handleGenerateDescription = async (product) => {
    try {
      setIsGenerating(true);
      // Используем выбранный продукт или текущий продукт
      const productToUse = selectedProduct || product;

      // Проверяем, что у продукта есть категория
      if (!productToUse.category || !productToUse.category.name) {
        throw new Error("У продукта не указана категория");
      }

      // Создаем копию продукта с правильной категорией
      const productWithCategory = {
        ...productToUse,
        game: productToUse.category.name, // Используем название категории как название игры
      };

      const generatedDescription = await generateProductDescription(
        productWithCategory
      );
      setFormData((prev) => ({
        ...prev,
        description: generatedDescription,
      }));
      setSnackbar({
        open: true,
        message: "Описание успешно сгенерировано",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const MobileProductCard = ({ product }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          {product.images && product.images[0] ? (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={`${STATIC_URL}/${product.images[0]}`}
                alt={product.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                flexShrink: 0,
              }}
            >
              <ImageIcon sx={{ fontSize: 40, color: "text.secondary" }} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" gutterBottom>
              {product.name}
            </Typography>
            <Chip
              label={product.category?.name || "Uncategorized"}
              size="small"
              color="secondary"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {product.description}
            </Typography>
            {product.systemRequirements &&
              product.systemRequirements.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    System Requirements:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {Array.isArray(product.systemRequirements) &&
                      product.systemRequirements.map((req, index) => {
                        const cleanReq =
                          typeof req === "string"
                            ? req.replace(/[\[\]"]/g, "").trim()
                            : "";
                        return cleanReq ? (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5, whiteSpace: "pre-line" }}
                          >
                            {cleanReq}
                          </Typography>
                        ) : null;
                      })}
                  </Box>
                </Box>
              )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Tooltip title="Копировать для XenForo">
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(product)}
              sx={{ mr: 1 }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => handleEdit(product)}
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(product._id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "1.5rem", sm: "2rem" },
            fontWeight: "bold",
          }}
        >
          Products
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: isMobile ? "100%" : "auto",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            size="small"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ width: isMobile ? "100%" : 200 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedProduct(null);
              setOpenDialog(true);
            }}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {isMobile ? (
        <Box>
          {filteredProducts.map((product) => (
            <MobileProductCard key={product._id} product={product} />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product._id}>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>
                    {product.images && product.images[0] && (
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={`${STATIC_URL}/${product.images[0]}`}
                          alt={product.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.category?.name || "Uncategorized"}
                      color="secondary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.status}
                      color={
                        product.status === "Undetected"
                          ? "success"
                          : product.status === "Detected"
                          ? "error"
                          : product.status === "On Update"
                          ? "warning"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{product.description}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Копировать для XenForo">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyToClipboard(product)}
                        sx={{ mr: 1 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(product)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(product._id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedProduct ? "Edit Product" : "Add New Product"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              margin="dense"
              required
            />
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category || ""}
                onChange={handleInputChange}
                label="Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="Undetected">Undetected</MenuItem>
                <MenuItem value="On Update">On Update</MenuItem>
                <MenuItem value="Detected">Detected</MenuItem>
                <MenuItem value="Use at own risk">Use at own risk</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                margin="dense"
                multiline
                rows={4}
              />
              <Button
                variant="outlined"
                onClick={() => handleGenerateDescription(formData)}
                disabled={isGenerating || !formData.name || !formData.category}
                sx={{ height: 56 }}
              >
                {isGenerating ? "Генерация..." : "Сгенерировать"}
              </Button>
            </Box>
            <TextField
              fullWidth
              label="System Requirements"
              name="systemRequirements"
              value={
                Array.isArray(formData.systemRequirements)
                  ? formData.systemRequirements.join("\n")
                  : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                // Разделяем по переносам строк, удаляем пустые строки, но сохраняем оригинальные строки без trim()
                setFormData((prev) => ({
                  ...prev,
                  systemRequirements: value
                    .split("\n")
                    .filter((line) => line.trim() !== ""), // Удаляем только полностью пустые строки
                }));
              }}
              margin="dense"
              multiline
              rows={4}
              helperText="Enter each requirement on a new line"
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Features
              </Typography>
              {formData.features.map((feature, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <TextField
                    label="Name"
                    size="small"
                    value={feature.name}
                    onChange={(e) => {
                      const newFeatures = [...formData.features];
                      newFeatures[index].name = e.target.value;
                      setFormData({ ...formData, features: newFeatures });
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Description"
                    size="small"
                    multiline
                    rows={4}
                    value={feature.description}
                    onChange={(e) => {
                      const newFeatures = [...formData.features];
                      newFeatures[index].description = e.target.value;
                      setFormData({ ...formData, features: newFeatures });
                    }}
                    sx={{ flex: 2 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newFeatures = formData.features.filter(
                        (_, i) => i !== index
                      );
                      setFormData({ ...formData, features: newFeatures });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({
                    ...formData,
                    features: [
                      ...formData.features,
                      { name: "", description: "" },
                    ],
                  });
                }}
              >
                Add Feature
              </Button>
            </Box>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Payment Links
              </Typography>
              {formData.paymentLinks.map((link, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}
                >
                  <TextField
                    label="Days"
                    type="number"
                    size="small"
                    value={link.days}
                    onChange={(e) => {
                      const newLinks = [...formData.paymentLinks];
                      newLinks[index].days = e.target.value;
                      setFormData({ ...formData, paymentLinks: newLinks });
                    }}
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label="Price"
                    type="number"
                    size="small"
                    value={link.price}
                    onChange={(e) => {
                      const newLinks = [...formData.paymentLinks];
                      newLinks[index].price = e.target.value;
                      setFormData({ ...formData, paymentLinks: newLinks });
                    }}
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label="URL"
                    size="small"
                    value={link.url}
                    onChange={(e) => {
                      const newLinks = [...formData.paymentLinks];
                      newLinks[index].url = e.target.value;
                      setFormData({ ...formData, paymentLinks: newLinks });
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Info"
                    size="small"
                    value={link.info}
                    onChange={(e) => {
                      const newLinks = [...formData.paymentLinks];
                      newLinks[index].info = e.target.value;
                      setFormData({ ...formData, paymentLinks: newLinks });
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newLinks = formData.paymentLinks.filter(
                        (_, i) => i !== index
                      );
                      setFormData({ ...formData, paymentLinks: newLinks });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({
                    ...formData,
                    paymentLinks: [
                      ...formData.paymentLinks,
                      { days: "", price: "", url: "", info: "" },
                    ],
                  });
                }}
              >
                Add Payment Link
              </Button>
            </Box>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {formData.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => {
                      const newTags = formData.tags.filter(
                        (_, i) => i !== index
                      );
                      setFormData({ ...formData, tags: newTags });
                    }}
                    size="small"
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Enter tag and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      e.preventDefault();
                      const newTag = e.target.value.trim();
                      if (!formData.tags.includes(newTag)) {
                        setFormData({
                          ...formData,
                          tags: [...formData.tags, newTag],
                        });
                      }
                      e.target.value = "";
                    }
                  }}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    const input =
                      e.target.previousSibling.querySelector("input");
                    if (input && input.value.trim()) {
                      const newTag = input.value.trim();
                      if (!formData.tags.includes(newTag)) {
                        setFormData({
                          ...formData,
                          tags: [...formData.tags, newTag],
                        });
                      }
                      input.value = "";
                    }
                  }}
                >
                  Add
                </Button>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isBestSeller}
                  onChange={(e) =>
                    setFormData({ ...formData, isBestSeller: e.target.checked })
                  }
                />
              }
              label="Best Seller"
              sx={{ mt: 1 }}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Images
              </Typography>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="images-upload"
                multiple
                type="file"
                onChange={(e) => setImageFiles([...e.target.files])}
              />
              <label htmlFor="images-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AddIcon />}
                >
                  Upload Images
                </Button>
              </label>
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {existingImages.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                    }}
                  >
                    <img
                      src={`${STATIC_URL}/${image}`}
                      alt={`Product ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "background.paper",
                      }}
                      onClick={() => {
                        setExistingImages(
                          existingImages.filter((_, i) => i !== index)
                        );
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {imageFiles.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                    }}
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "background.paper",
                      }}
                      onClick={() => {
                        setImageFiles(imageFiles.filter((_, i) => i !== index));
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Video
              </Typography>
              <TextField
                fullWidth
                label="Video URL"
                name="videoUrl"
                value={formData.video.url}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    video: { ...formData.video, url: e.target.value },
                  })
                }
                margin="dense"
              />
              <Box sx={{ mt: 1 }}>
                <input
                  accept="video/*"
                  style={{ display: "none" }}
                  id="video-upload"
                  type="file"
                  onChange={(e) => setVideoFile(e.target.files[0])}
                />
                <label htmlFor="video-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                  >
                    Upload Video File
                  </Button>
                </label>
                {videoFile && (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {videoFile.name}
                    </Typography>
                    <IconButton size="small" onClick={() => setVideoFile(null)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.category}
          >
            {selectedProduct ? "Save Changes" : "Add Product"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Products;
